// src/components/BuySell.js

import React, { useEffect, useState } from "react";

import {
  CreditCard,
  Apple,
  ShieldCheck,
  Lock,
  Check,
  ChevronDown,
  Info,
  ArrowLeft,
  X,
} from "lucide-react";

import { useNavigate } from "react-router-dom";

import BaitLogo from "../BaitLogo.png";

import "./BuySell.css";

// =========================================================
// API
// =========================================================

const GOLD_API =
  "https://goldapi.bait-coin.com/api/gold-reference";

// =========================================================
// BAIT CONFIGURATION
// =========================================================

const BAIT_GOLD_GRAMS = 4.25;

// =========================================================
// BUY SELL COMPONENT
// =========================================================

const BuySell = () => {
  const navigate = useNavigate();

  // =======================================================
  // MARKET DATA
  // =======================================================

  const [goldPricePerGram, setGoldPricePerGram] =
    useState(0);

  const [baitPrice, setBaitPrice] =
    useState(0);

  const [priceLoading, setPriceLoading] =
    useState(true);

  const [priceError, setPriceError] =
    useState("");

  // =======================================================
  // FORM
  // =======================================================

  const [spendAmount, setSpendAmount] =
    useState("100");

  const [paymentMethod, setPaymentMethod] =
    useState("");

  // =======================================================
  // PAYMENT PANEL
  // =======================================================

  const [showPaymentSection, setShowPaymentSection] =
    useState(false);

  // =======================================================
  // CARD DATA
  // =======================================================

  const [cardNumber, setCardNumber] =
    useState("");

  const [cardName, setCardName] =
    useState("");

  const [expiry, setExpiry] =
    useState("");

  const [cvv, setCvv] =
    useState("");

  // =======================================================
  // PAYMENT STATUS
  // =======================================================

  const [paymentMessage, setPaymentMessage] =
    useState("");

  const [processing, setProcessing] =
    useState(false);

  const [paymentComplete, setPaymentComplete] =
    useState(false);

  // =======================================================
  // FETCH LIVE GOLD PRICE
  // =======================================================

  const fetchGoldPrice = async () => {
    try {
      setPriceLoading(true);
      setPriceError("");

      const response =
        await fetch(GOLD_API);

      if (!response.ok) {
        throw new Error(
          `Gold API request failed: ${response.status}`
        );
      }

      const data =
        await response.json();

      const gramPrice =
        Number(
          data?.gold?.usd_per_gram
        ) || 0;

      if (!gramPrice) {
        throw new Error(
          "Invalid gold price received."
        );
      }

      // ===================================================
      // 1 BAIT = 4.25 GRAMS OF GOLD
      // ===================================================

      const calculatedBaitPrice =
        gramPrice * BAIT_GOLD_GRAMS;

      setGoldPricePerGram(
        gramPrice
      );

      setBaitPrice(
        calculatedBaitPrice
      );

    } catch (error) {
      console.error(
        "BuySell gold price error:",
        error
      );

      setPriceError(
        "Unable to load current gold price."
      );

    } finally {
      setPriceLoading(false);
    }
  };

  // =======================================================
  // INITIAL LOAD
  // =======================================================

  useEffect(() => {
    fetchGoldPrice();

    const interval =
      setInterval(
        fetchGoldPrice,
        60000
      );

    return () => {
      clearInterval(interval);
    };
  }, []);

  // =======================================================
  // FORMAT USD
  // =======================================================

  const formatUSD = (value) => {
    const number =
      Number(value);

    if (!Number.isFinite(number)) {
      return "$0.00";
    }

    return number.toLocaleString(
      "en-US",
      {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );
  };

  // =======================================================
  // FORMAT CARD NUMBER
  // =======================================================

  const formatCardNumber = (value) => {
    const numbers =
      value
        .replace(/\D/g, "")
        .slice(0, 16);

    return numbers
      .replace(
        /(.{4})/g,
        "$1 "
      )
      .trim();
  };

  // =======================================================
  // FORMAT EXPIRY
  // =======================================================

  const formatExpiry = (value) => {
    const numbers =
      value
        .replace(/\D/g, "")
        .slice(0, 4);

    if (numbers.length >= 3) {
      return `${numbers.slice(
        0,
        2
      )}/${numbers.slice(2)}`;
    }

    return numbers;
  };

  // =======================================================
  // SPEND VALUE
  // =======================================================

  const numericSpend =
    Number(spendAmount) || 0;

  // =======================================================
  // BAIT RECEIVED
  // =======================================================

  const baitReceived =
    baitPrice > 0
      ? numericSpend / baitPrice
      : 0;

  // =======================================================
  // PAYMENT FEE
  // =======================================================

  const paymentFee =
    numericSpend > 0
      ? numericSpend * 0.029
      : 0;

  // =======================================================
  // TOTAL PAYMENT
  // =======================================================

  const totalPayment =
    numericSpend + paymentFee;

  // =======================================================
  // QUICK AMOUNTS
  // =======================================================

  const quickAmounts = [
    50,
    100,
    250,
    500,
    1000,
  ];

  // =======================================================
  // SELECT PAYMENT METHOD
  // =======================================================

  const handlePaymentMethod = (method) => {
    setPaymentMethod(method);

    setPaymentMessage("");

    setPaymentComplete(false);

    if (method === "card") {
      setShowPaymentSection(false);
    }
  };

  // =======================================================
  // CONTINUE PAYMENT
  // =======================================================

  const handleContinuePayment = () => {
    setPaymentMessage("");

    if (
      !numericSpend ||
      numericSpend <= 0
    ) {
      setPaymentMessage(
        "Please enter a valid amount."
      );

      return;
    }

    if (!paymentMethod) {
      setPaymentMessage(
        "Please select a payment method."
      );

      return;
    }

    if (paymentMethod === "card") {
      return;
    }

    if (paymentMethod === "applepay") {
      setShowPaymentSection(true);
    }
  };

  // =======================================================
  // CARD VALIDATION
  // =======================================================

  const validateCard = () => {
    const cleanCard =
      cardNumber.replace(
        /\s/g,
        ""
      );

    if (
      cleanCard.length < 13
    ) {
      setPaymentMessage(
        "Please enter a valid card number."
      );

      return false;
    }

    if (
      !cardName.trim()
    ) {
      setPaymentMessage(
        "Please enter the cardholder name."
      );

      return false;
    }

    if (
      expiry.length !== 5
    ) {
      setPaymentMessage(
        "Please enter a valid expiry date."
      );

      return false;
    }

    if (
      cvv.length < 3
    ) {
      setPaymentMessage(
        "Please enter a valid CVV."
      );

      return false;
    }

    return true;
  };

  // =======================================================
  // PROCESS PAYMENT
  // =======================================================

  const handleProcessPayment = async () => {
    setPaymentMessage("");
    setPaymentComplete(false);

    if (
      paymentMethod === "card"
    ) {
      if (!validateCard()) {
        return;
      }

      setProcessing(true);

      setTimeout(() => {
        setProcessing(false);

        setPaymentComplete(true);

        setPaymentMessage(
          "Payment request created successfully."
        );
      }, 1200);

      return;
    }

    if (
      paymentMethod === "applepay"
    ) {
      setProcessing(true);

      setTimeout(() => {
        setProcessing(false);

        setPaymentComplete(true);

        setPaymentMessage(
          "Apple Pay payment request created successfully."
        );
      }, 1200);
    }
  };

  // =======================================================
  // CLOSE PAYMENT MODAL
  // =======================================================

  const closePaymentSection = () => {
    setShowPaymentSection(false);

    setPaymentMessage("");
    setPaymentComplete(false);
  };

  // =======================================================
  // PRICE DISPLAY
  // =======================================================

  const baitPriceDisplay =
    priceLoading
      ? "Loading..."
      : formatUSD(baitPrice);

  // =======================================================
  // RETURN
  // =======================================================

  return (
    <div className="buysell-page">

      <div className="buysell-content">

        {/* =================================================
            PAGE TITLE
        ================================================= */}

        <div className="buysell-page-title">

          {/* BACK BUTTON */}
          <button
            type="button"
            className="market-back-btn"
            onClick={() => navigate("/yourwallet")}
            title="Back to Wallet"
          >
            <ArrowLeft size={18} />
          </button>

          <div className="buysell-title-text">
            <h1
              style={{
                margin: 0,
                fontFamily: "'Inter', 'Poppins', 'Segoe UI', sans-serif",
                fontSize: "26px",
                fontWeight: 600,
                letterSpacing: "1.4px",
                lineHeight: 1.1,
                color: "#8a5a1f",
                WebkitTextStroke: "0.4px #6b3d0a",
                textShadow:
                  "0 1px 0 rgba(255, 240, 200, 0.6), 0 2px 6px rgba(154, 106, 24, 0.22)",
              }}
            >
              Buy Crypto
            </h1>

            <p
              style={{
                margin: "4px 0 0",
                fontFamily: "'Poppins', 'Segoe UI', sans-serif",
                fontSize: "11px",
                fontWeight: 500,
                letterSpacing: "0.6px",
                lineHeight: 1.3,
                color: "#a07a3f",
                opacity: 0.95,
              }}
            >
              Buy BAIT securely with fiat
            </p>
          </div>

        </div>


        {/* =================================================
            SECURITY
        ================================================= */}

        <div className="buysell-secure">

          <div>
          </div>

          <span className="secure-dot" />

        </div>


        {/* =================================================
            ERROR
        ================================================= */}

        {priceError && (
          <div className="buysell-error">
            {priceError}
          </div>
        )}


        {/* =================================================
            MAIN
        ================================================= */}

        <div className="buysell-layout">


          {/* =================================================
              BUY FORM
          ================================================= */}

          <div className="buysell-main-card">


            {/* =================================================
                SPEND
            ================================================= */}

            <div className="buy-field-section">

              <label>
                You spend
              </label>

              <div className="amount-input-row">

                <input
                  type="number"
                  min="1"
                  value={spendAmount}
                  onChange={(e) =>
                    setSpendAmount(
                      e.target.value
                    )
                  }
                />

                <div className="currency-select">

                  <span>
                    🇺🇸
                  </span>

                  USD

                  <ChevronDown size={14} />

                </div>

              </div>

            </div>


            {/* =================================================
                QUICK AMOUNTS
            ================================================= */}

            <div className="quick-amounts">

              {quickAmounts.map(
                (amount) => (
                  <button
                    key={amount}
                    type="button"
                    className={
                      Number(
                        spendAmount
                      ) === amount
                        ? "quick-amount active"
                        : "quick-amount"
                    }
                    onClick={() =>
                      setSpendAmount(
                        String(amount)
                      )
                    }
                  >
                    ${amount}
                  </button>
                )
              )}

            </div>


            {/* =================================================
                RECEIVE
            ================================================= */}

            <div className="buy-field-section receive-section">

              <label>
                You receive
              </label>

              <div className="amount-input-row">

                <input
                  type="text"
                  readOnly
                  value={
                    baitReceived > 0
                      ? baitReceived.toFixed(4)
                      : "0.0000"
                  }
                />

                <div className="currency-select bait-select">

                  <img
                    src={BaitLogo}
                    alt="BAIT"
                  />

                  BAIT

                  <ChevronDown size={14} />

                </div>

              </div>

              <div className="price-reference">

                1 BAIT ={" "}
                {baitPriceDisplay}

                <Info size={12} />

              </div>

              <div className="gold-reference">

                Live Gold Reference:{" "}
                {formatUSD(
                  goldPricePerGram
                )} / gram

              </div>

            </div>


            {/* =================================================
                PAYMENT METHODS
            ================================================= */}

            <div className="payment-method-section">

              <div className="section-heading">
                Payment Method
              </div>


              {/* =================================================
                  CREDIT CARD
              ================================================= */}

              <button
                type="button"
                className={`payment-option ${
                  paymentMethod === "card"
                    ? "selected"
                    : ""
                }`}
                onClick={() =>
                  handlePaymentMethod("card")
                }
              >

                <div className="payment-option-icon">

                  <CreditCard
                    size={19}
                  />

                </div>

                <div className="payment-option-content">

                  <strong>
                    Credit Card
                  </strong>

                  <span>
                    Visa, Mastercard, American Express
                  </span>

                </div>

                <div className="payment-radio">

                  {paymentMethod ===
                    "card" && (
                    <Check size={12} />
                  )}

                </div>

              </button>


              {/* =================================================
                  INLINE CARD PAYMENT
              ================================================= */}

              {paymentMethod === "card" && (

                <div className="inline-card-payment">

                  <div className="card-payment-form">


                    {/* CARD PREVIEW */}

                    <div className="card-preview">

                      <div className="card-chip" />

                      <span>
                        BAIT WALLET
                      </span>

                      <strong>
                        {cardNumber ||
                          "•••• •••• •••• ••••"}
                      </strong>

                      <div className="card-preview-labels">

                        <small>
                          CARD HOLDER
                        </small>

                        <small>
                          EXPIRES
                        </small>

                      </div>

                      <div className="card-preview-values">

                        <span>
                          {cardName ||
                            "YOUR NAME"}
                        </span>

                        <span>
                          {expiry ||
                            "MM/YY"}
                        </span>

                      </div>

                    </div>


                    {/* CARD NUMBER */}

                    <label>
                      Card Number
                    </label>

                    <div className="payment-input">

                      <CreditCard
                        size={17}
                      />

                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="cc-number"
                        placeholder="1234 5678 9012 3456"
                        value={cardNumber}
                        onChange={(e) =>
                          setCardNumber(
                            formatCardNumber(
                              e.target.value
                            )
                          )
                        }
                      />

                    </div>


                    {/* CARDHOLDER NAME */}

                    <label>
                      Cardholder Name
                    </label>

                    <input
                      className="payment-text-input"
                      type="text"
                      autoComplete="cc-name"
                      placeholder="Name on card"
                      value={cardName}
                      onChange={(e) =>
                        setCardName(
                          e.target.value
                        )
                      }
                    />


                    {/* EXPIRY + CVV */}

                    <div className="card-small-fields">

                      <div>

                        <label>
                          Expiry
                        </label>

                        <input
                          className="payment-text-input"
                          type="text"
                          inputMode="numeric"
                          autoComplete="cc-exp"
                          placeholder="MM/YY"
                          value={expiry}
                          onChange={(e) =>
                            setExpiry(
                              formatExpiry(
                                e.target.value
                              )
                            )
                          }
                        />

                      </div>


                      <div>

                        <label>
                          CVV
                        </label>

                        <input
                          className="payment-text-input"
                          type="password"
                          inputMode="numeric"
                          autoComplete="cc-csc"
                          placeholder="•••"
                          maxLength={4}
                          value={cvv}
                          onChange={(e) =>
                            setCvv(
                              e.target.value
                                .replace(
                                  /\D/g,
                                  ""
                                )
                                .slice(
                                  0,
                                  4
                                )
                            )
                          }
                        />

                      </div>

                    </div>


                    {/* PAYMENT MESSAGE */}

                    {paymentMessage && (
                      <div
                        className={
                          paymentComplete
                            ? "payment-success"
                            : "payment-message"
                        }
                      >

                        {paymentComplete && (
                          <Check size={16} />
                        )}

                        {paymentMessage}

                      </div>
                    )}


                    {/* PAY NOW */}

                    <button
                      type="button"
                      className="pay-now-btn"
                      onClick={
                        handleProcessPayment
                      }
                      disabled={processing}
                    >

                      {processing
                        ? "Processing..."
                        : `Pay ${formatUSD(
                            totalPayment
                          )}`}

                    </button>


                    <div className="payment-security">

                      <Lock size={12} />

                      Secure encrypted payment

                    </div>

                  </div>

                </div>

              )}


              {/* =================================================
                  APPLE PAY
              ================================================= */}

              <button
                type="button"
                className={`payment-option ${
                  paymentMethod === "applepay"
                    ? "selected"
                    : ""
                }`}
                onClick={() =>
                  handlePaymentMethod(
                    "applepay"
                  )
                }
              >

                <div className="payment-option-icon apple-icon">

                  <Apple size={19} />

                </div>

                <div className="payment-option-content">

                  <strong>
                    Apple Pay
                  </strong>

                  <span>
                    Fast and secure payment
                  </span>

                </div>

                <div className="payment-radio">

                  {paymentMethod ===
                    "applepay" && (
                    <Check size={12} />
                  )}

                </div>

              </button>

            </div>


            {/* =================================================
                CONTINUE
            ================================================= */}

            <button
              className="continue-payment-btn"
              onClick={
                handleContinuePayment
              }
              type="button"
            >

              {paymentMethod
                ? `Continue with ${
                    paymentMethod ===
                    "card"
                      ? "Card"
                      : "Apple Pay"
                  }`
                : "Continue to Payment"}

            </button>


            <div className="secure-note">

              <Lock size={12} />

              Secure and encrypted transaction

            </div>

          </div>


          {/* =================================================
              ORDER SUMMARY
          ================================================= */}

          <div className="order-summary-card">

            <h2>
              Order Summary
            </h2>

            <div className="summary-row">

              <span>
                You Spend
              </span>

              <strong>
                {formatUSD(
                  numericSpend
                )}
              </strong>

            </div>

            <div className="summary-row">

              <span>
                Fees <Info size={11} />
              </span>

              <strong>
                {formatUSD(
                  paymentFee
                )}
              </strong>

            </div>

            <div className="summary-divider" />

            <div className="summary-row receive-row">

              <span>
                You Receive
              </span>

              <strong>
                {baitReceived.toFixed(4)}
                {" "}BAIT
              </strong>

            </div>

            <div className="summary-row">

              <span>
                BAIT Price
              </span>

              <strong>
                {formatUSD(
                  baitPrice
                )}
              </strong>

            </div>


            {/* PRICE GUARANTEE */}

            <div className="price-guarantee">

              <ShieldCheck size={18} />

              <div>

                <strong>
                  Live Gold Pricing
                </strong>

                <span>
                  BAIT value is calculated
                  from the live Gold Reference
                  price.
                </span>

              </div>

            </div>


            {/* WHY BAIT */}

            <div className="why-bait">

              <h3>
                Why Buy with BAIT Wallet?
              </h3>

              <div>
                ✓ Gold-backed digital asset
              </div>

              <div>
                ✓ Secure and non-custodial
              </div>

              <div>
                ✓ Funds delivered to your wallet
              </div>

              <div>
                ✓ Live gold reference pricing
              </div>

            </div>


            {/* SUPPORTED CURRENCIES */}

            <div className="supported-currencies">

              <div className="currency-icons">
              </div>

            </div>

          </div>

        </div>

      </div>


      {/* =================================================
          APPLE PAY PAYMENT MODAL
      ================================================= */}

      {showPaymentSection && (
        <div className="payment-overlay">

          <div className="payment-modal">


            {/* HEADER */}

            <div className="payment-modal-header">

              <button
                type="button"
                onClick={
                  closePaymentSection
                }
                className="payment-back-btn"
              >
                <ArrowLeft
                  size={18}
                />
              </button>

              <div>

                <h2>
                  Apple Pay
                </h2>

                <p>
                  Pay{" "}
                  {formatUSD(
                    totalPayment
                  )}
                </p>

              </div>

              <button
                type="button"
                onClick={
                  closePaymentSection
                }
                className="payment-close-btn"
              >
                <X size={18} />
              </button>

            </div>


            {/* APPLE PAY */}

            <div className="apple-pay-panel">

              <div className="apple-pay-logo">

                <Apple
                  size={42}
                  fill="currentColor"
                />

              </div>

              <h3>
                Apple Pay
              </h3>

              <p>
                Pay securely using Apple
                Pay. Your card information
                remains protected by Apple Pay.
              </p>

              <div className="apple-order">

                <span>
                  You receive
                </span>

                <strong>
                  {baitReceived.toFixed(4)}
                  {" "}BAIT
                </strong>

              </div>

              <div className="apple-order">

                <span>
                  Total
                </span>

                <strong>
                  {formatUSD(
                    totalPayment
                  )}
                </strong>

              </div>

              <button
                type="button"
                className="apple-pay-btn"
                onClick={
                  handleProcessPayment
                }
                disabled={processing}
              >

                <Apple
                  size={21}
                  fill="currentColor"
                />

                {processing
                  ? "Processing..."
                  : "Pay with Apple Pay"}

              </button>

              {paymentMessage && (
                <div
                  className={
                    paymentComplete
                      ? "payment-success"
                      : "payment-message"
                  }
                >

                  {paymentComplete && (
                    <Check size={17} />
                  )}

                  {paymentMessage}

                </div>
              )}

            </div>


            <div className="payment-security">

              <Lock size={13} />

              Secure encrypted payment

            </div>

          </div>

        </div>
      )}

    </div>
  );
};

export default BuySell;